import React, { useState, useEffect, useRef } from "react";
import { ArrowLeft, CalendarDays, ChevronDown } from "lucide-react";
import { HTTP } from "../api/http-common";
import { useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import { isValidEmail } from '../component/common';

function EventCommonView({
    event,
    dropdownOpen,
    setDropdownOpen,
    getGoogleCalendarUrl,
    getOutlookCalendarUrl,
    getICalendarUrl,
    formatTime,
    adminView = false
}) {
    const navigate = useNavigate();
    const [emailError, setEmailError] = useState("");
    const [nameError, setTitleError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    const [dateError, setDateError] = useState("");
    const [businessnameError, setBusinessNameError] = useState("");
    const [contactError, setContactError] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false); // Track form submission
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        contact_no: "",
        business_name: "",
        business_description: "",
        interested_date: "",
    });

    const validateField = () => {
        resetValidationErrors();
        let checked = true;

        // Name Validation
        if (!formData.name.trim()) {
            setTitleError('Please enter a name');
            checked = false;
        } else if (formData.name.length < 5) {
            setTitleError("Name must be at least 5 characters");
            checked = false;
        } else if (formData.name.length > 100) {
            setTitleError("Name must not exceed 100 characters");
            checked = false;
        }

        // Business Name Validation
        if (!formData.business_name.trim()) {
            setBusinessNameError('Please enter a business name');
            checked = false;
        } else if (formData.business_name.length < 5) {
            setBusinessNameError("Business name must be at least 5 characters");
            checked = false;
        } else if (formData.business_name.length > 100) {
            setBusinessNameError("Business name must not exceed 100 characters");
            checked = false;
        }

        // Business Description Validation
        if (!formData.business_description.trim()) {
            setDescriptionError('Please enter a business description');
            checked = false;
        } else if (formData.business_description.length < 5) {
            setDescriptionError("Description must be at least 5 characters");
            checked = false;
        }

        // Contact Number Validation
        if (String(formData.contact_no).trim() === '') {
            setContactError('Please enter phone number');
            checked = false;
        } else if (String(formData.contact_no).length > 10) {
            setContactError("Max character limit is 10");
            checked = false;
        }

        // Email Validation
        if (formData.email.trim() === '') {
            setEmailError('Please enter email address');
            checked = false;
        } else if (!isValidEmail(formData.email)) {
            setEmailError('Invalid email.');
            checked = false;
        } else if (formData.email.length > 100) {
            setEmailError("Max character limit is 100");
            checked = false;
        }

        // Date Validation
        if (!formData.interested_date) {
            setDateError("Please enter a start date");
            checked = false;
        }

        return checked;
    };


    const resetValidationErrors = () => {
        setTitleError('');
        setDescriptionError('');
        setDateError('');
        setBusinessNameError('');
        setContactError('');
        setEmailError('');
    };


    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleEmailChange = (e) => {
        setEmailError("");
        handleChange(e);
    };

    const handleSubmit = async (e) => {
        e.preventDefault(); // Prevent default form submission

        if (validateField()) {
            try {
                let fields = {
                    name: formData.name,
                    email: formData.email,
                    contact_no: formData.contact_no,
                    business_name: formData.business_name,
                    business_description: formData.business_description,
                    interested_date: formData.interested_date
                };

                const response = await HTTP.post("/user/sponsorship-inquiry", fields);

                if (response.status === 200) {
                    Swal.fire({
                        title: "We will Contact You Soon...!",
                        icon: "success"
                    }).then(() => {
                        setIsSubmitted(true); // Hide form after alert confirmation
                    });
                    setFormData({
                        name: "",
                        email: "",
                        contact_no: "",
                        business_name: "",
                        business_description: "",
                        interested_date: "",
                    });
                }
            } catch (error) {
                if (error.response) {
                    if (error.response.status === 406) {
                        resetValidationErrors();
                    }
                    if (error.response.status === 400) {
                        Swal.fire({
                            title: "Error!",
                            text: "You are already registered!",
                            icon: "error",
                            confirmButtonText: "Ok"
                        });
                    }
                }
            }
        }
    };

    return (
        // <div className="container mx-auto p-8">
        <div className={`container mx-auto ${adminView == 0 ? 'p-8 common-ui-section-design my-4':null}`}>
            {/* Event Header */}
            {adminView == 0 ? <p className="tribe-events-back">
                <div className="">

                    <Link
                        className="custom-theme-link-button flexbox justify-between w-[130px] px-3 py-1.5 "
                        to="/event-calender"
                    >
                        <span><ArrowLeft /></span>
                        <span>All Events</span>
                    </Link>
                </div>
            </p> : ''}
            &nbsp;
            <div className="flex flex-col md:flex-row items-start">
                {event.image && (
                    <img
                        src={event.image}
                        alt={event.title}
                        className="w-1/3 md:w-1/4 rounded-lg shadow-lg"
                    />
                )}

                {/* Event Details */}
                <div className="text-left ml-6">
                    <h1 className="text-4xl font-bold">{event.title}</h1>
                    <p className="text-lg text-gray-600 mt-2">
                        {event.formatted_datetime ||
                            `${event.start_date} @ ${event.start_time} - ${event.end_time}`}{" "}
                        |{" "}
                        <strong>
                            Price: <span className="theme-color"> {!event.price || event.price === "null" ? 'Free' : `$${event.price}`}</span>
                        </strong>
                    </p>

                    {/* Dropdown Button for Calendar Options */}
                    <div className="flex flex-wrap gap-4 mt-4">
                        {/* Add to Calendar Button */}
                        <div className="relative">
                            <button
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="px-4 py-2 theme-button text-white flex items-center gap-2 rounded-md shadow-md  transition"
                            >
                                <CalendarDays className="w-5 h-5" /> Add to Calendar <ChevronDown className="w-4 h-4" />
                            </button>

                            {dropdownOpen && (
                                <div className="absolute mt-2 w-48 bg-white border rounded-lg shadow-lg z-10">
                                    <a
                                        href={getGoogleCalendarUrl()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block px-4 py-2 hover:bg-gray-100"
                                    >
                                        Google Calendar
                                    </a>
                                    <a
                                        href={getOutlookCalendarUrl()}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="block px-4 py-2 hover:bg-gray-100"
                                    >
                                        Outlook Calendar
                                    </a>
                                    <a
                                        href={getICalendarUrl()}
                                        download={`${event.title}.ics`}
                                        className="block px-4 py-2 hover:bg-gray-100"
                                    >
                                        iCalendar
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Register Button (Appears only if event URL exists) */}

                        {event.event_url && (
                            <a
                                href={event.event_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="px-4 py-2 theme-button text-white flex items-center gap-2 rounded-md shadow-md  transition"
                            >
                                Register
                            </a>
                        )}
                    </div>

                </div>
            </div>

            {/* Event Description */}
            <div className="mt-6 text-gray-700">
                <p
                    dangerouslySetInnerHTML={{
                        __html: event.description.replace(/\. /g, ".<br /><br />"),
                    }}
                ></p>
            </div>


            {/* Event Info Grid */}
            <div className="grid md:grid-cols-3 gap-6 mt-8 border-t pt-6">
                <div>
                    <h2 className="text-xl font-bold">DETAILS</h2>
                    <p>
                        <strong>Date:</strong> <br />
                        {event.start_date_formatted}
                    </p>
                    <p>
                        <strong>Time:</strong> <br />
                        {formatTime(event.start_time)} - {formatTime(event.end_time)}
                    </p>
                    <p>
                        <strong>Cost:</strong> <br />
                        {!event.price || event.price === "null" ? "Free" : `$${event.price}`}
                    </p>
                </div>

                {/* Venue Section */}
                <div>
                    <h2 className="text-xl font-bold">VENUE</h2>
                    <p>
                        {event.address}, {event.state}
                    </p>

                    {/* Google Map */}
                    <iframe
                        className="w-full mt-3 rounded-md"
                        src={`https://www.google.com/maps?q=${event.latitude},${event.longitude}&output=embed`}
                        height="200"
                        allowFullScreen
                        loading="lazy"
                    ></iframe>

                </div>
            </div>
            {adminView == 0 && <>{!isSubmitted && (
                <form className="space-y-4 mt-8 basic-theme-layout" onSubmit={handleSubmit} encType="multipart/form-data">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-2xl">Become Vendor/Sponsor</h3>
                        {/* <h3 className="font-medium text-lg">Vendor/Sponsorship Application</h3> */}
                    </div>

                    <div className="grid grid-cols-2 gap-4 px-2">
                        {/* Name */}
                        <div className="space-y-2">
                            {/* <label htmlFor="name" className="text-sm font-medium"></label> */}
                            <input
                                type="text"
                                id="name"
                                name="name"
                                className="peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder="Name"
                            />
                            <div>{nameError && <span className="text-sm text-red-500">{nameError}</span>}</div>
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            {/* <label htmlFor="email" className="text-sm font-medium">Email</label> */}
                            <input
                                type="email"
                                id="email"
                                name="email"
                                className="peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400"
                                value={formData.email}
                                onChange={handleEmailChange}
                                placeholder="Email"
                            />
                            <div>{emailError && <span className="text-sm text-red-500">{emailError}</span>}</div>
                        </div>

                        {/* Contact No */}
                        <div className="space-y-2">
                            {/* <label htmlFor="contact_no" className="text-sm font-medium">Contact No</label> */}
                            <input
                                placeholder="Contact No"
                                type="number"
                                id="contact_no"
                                name="contact_no"
                                className="peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400"
                                value={formData.contact_no}
                                onChange={handleChange}
                            />
                            <div>{contactError && <span className="text-sm text-red-500">{contactError}</span>}</div>
                        </div>

                        {/* Business Name */}
                        <div className="space-y-2">
                            {/* <label htmlFor="business_name" className="text-sm font-medium"></label> */}
                            <input
                                placeholder="Business Name"
                                type="text"
                                id="business_name"
                                name="business_name"
                                className="peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400"
                                value={formData.business_name}
                                onChange={handleChange}
                            />
                            <div>{businessnameError && <span className="text-sm text-red-500">{businessnameError}</span>}</div>
                        </div>

                        {/* Business Description */}
                        <div className="space-y-2">
                            {/* <label htmlFor="business_description" className="text-sm font-medium"></label> */}
                            <textarea
                                placeholder="Business Description"
                                id="business_description"
                                name="business_description"
                                className="w-full p-3 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={formData.business_description}
                                onChange={handleChange}
                            />
                            <div>{descriptionError && <span className="text-sm text-red-500">{descriptionError}</span>}</div>
                        </div>

                        {/* Interested Date */}
                        <div className="space-y-2">
                            <label htmlFor="interested_date" className="text-gray-400  font-medium">Interested Date</label>
                            <input
                                // placeholder="Interested Date"
                                type="date"
                                id="interested_date"
                                name="interested_date"
                                className="peer w-full focus:outline-none focus:border-b focus:border-gray-400 placeholder:text-gray-400 h-10 px-0 bg-transparent border-0 border-b input-border-bottom rounded-none focus-visible:ring-0 focus-visible:border-gray-400"
                                value={formData.interested_date}
                                onChange={handleChange}
                            />
                            <div>{dateError && <span className="text-sm text-red-500">{dateError}</span>}</div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <div className="">
                        <button type="submit" className="theme-button text-white px-4 py-2 rounded-md shadow-sm">
                            Submit
                        </button>
                    </div>
                </form>
            )}</>}
            
        </div>
    );
}

export default EventCommonView;
